import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import EntityProperty from '@/common/models/EntityProperty';
import Exception from '@/common/models/Exception';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { getStore } from '@/globals';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

export interface IOrganizationCustomFieldValueApi {
  organizationcustomvalue_id: number;
  organization_id: number;
  organizationcustomfield_id: number;
  value: string;
}

export interface IOrganizationCustomFieldValue {
  organizationCustomValueId: number;
  organizationId: number;
  organizationCustomFieldId: number;
  value: string;
}

export function organizationCustomFieldValueLabel(plural = false) {
  return `${i18n.global.t('names.organizationCustomField')} ${i18n.global.t('names.value', { n: plural ? 2 : 1 })}`;
}

const type = 'OrganizationCustomFieldValue';

export default class OrganizationCustomFieldValue extends Validatable {
  organizationCustomValueId: number;
  organizationId: number;
  organizationCustomFieldId: number;
  value: string;

  constructor(organizationCustomValueId = -1, organizationId = -1, organizationCustomFieldId = -1, value = '') {
    super();

    this.id = organizationCustomValueId;
    this.organizationCustomValueId = organizationCustomValueId;
    this.organizationId = organizationId;
    this.organizationCustomFieldId = organizationCustomFieldId;
    this.value = value;
  }

  static fromObject(obj: IOrganizationCustomFieldValue): OrganizationCustomFieldValue {
    return new OrganizationCustomFieldValue(
      obj.organizationCustomValueId,
      obj.organizationId,
      obj.organizationCustomFieldId,
      obj.value,
    );
  }

  static fromApi(apiObject: IOrganizationCustomFieldValueApi): OrganizationCustomFieldValue {
    return new OrganizationCustomFieldValue(
      apiObject.organizationcustomvalue_id,
      apiObject.organization_id,
      apiObject.organizationcustomfield_id,
      apiObject.value,
    );
  }

  toApi(): IOrganizationCustomFieldValueApi {
    return {
      organizationcustomvalue_id: this.organizationCustomValueId,
      organization_id: this.organizationId,
      organizationcustomfield_id: this.organizationCustomFieldId,
      value: this.value,
    };
  }

  static get entityName(): string {
    return type;
  }

  static get entityProperties() {
    if (OrganizationCustomFieldValue.entityPropertiesCached) {
      return OrganizationCustomFieldValue.entityPropertiesCached;
    }

    const editableClassName = 'OrganizationCustomFieldValue';

    OrganizationCustomFieldValue.entityPropertiesCached = {
      id: new EntityProperty({
        label: '#',
        property: 'organizationcustomvalue_id',
        type: TableColumnTypes.integer,
        editablePropertyName: 'organizationCustomValueId',
        editableClassName,
      }),
      organizationId: new EntityProperty({
        label: i18n.global.t('names.organization'),
        property: 'organization_id',
        type: TableColumnTypes.integer,
        editablePropertyName: 'organizationId',
        editableClassName,
      }),
      organizationCustomFieldId: new EntityProperty({
        label: i18n.global.t('names.organizationCustomField'),
        property: 'organizationcustomfield_id',
        type: TableColumnTypes.integer,
        editablePropertyName: 'organizationCustomFieldId',
        editableClassName,
      }),
      value: new EntityProperty({
        label: capitalize(organizationCustomFieldValueLabel(true)),
        property: 'value',
        type: TableColumnTypes.string,
        editablePropertyName: 'value',
        editableClassName,
        mutation: null,
        action: async (entity: OrganizationCustomFieldValue) => {
          await getStore().dispatch('orgApp/updateOrganizationCustomFieldValue', { instance: entity });
        },
        isEditableHandler: (entity: OrganizationCustomFieldValue) => entity.organizationCustomValueId > 0,
      }),
    };

    return OrganizationCustomFieldValue.entityPropertiesCached;
  }

  static async patchEntities(entities: OrganizationCustomFieldValue[]): Promise<void> {
    try {
      await getStore().dispatch('orgApp/postOrganizationCustomFieldsValue', { instances: entities });
    } catch (e) {
      throw new Exception('Failed to upload organization custom fields values.');
    }
  }

  static get bulkEditProperties() {
    return [
      OrganizationCustomFieldValue.entityProperties.organizationCustomFieldId,
      OrganizationCustomFieldValue.entityProperties.value,
    ];
  }

  static get bulkV4Properties() {
    return [
      OrganizationCustomFieldValue.entityProperties.organizationCustomFieldId,
      OrganizationCustomFieldValue.entityProperties.value,
    ];
  }

  static async assignIDs(entities: OrganizationCustomFieldValue[]): Promise<OrganizationCustomFieldValue[]> {
    if (entities.length === 0) return [];

    console.log(entities);
    return entities.map((x) =>
      OrganizationCustomFieldValue.fromObject({
        ...x,
        organizationCustomFieldId: x?.rawValues?.organizationcustomfield_id
          ? Number(x?.rawValues?.organizationcustomfield_id)
          : -1,
        value: x?.rawValues?.value,
      }),
    );
  }

  static {
    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      OrganizationCustomFieldValue.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      OrganizationCustomFieldValue.entityPropertiesCached = null;
    });
  }
}
